import React, { useEffect, useState, lazy, Suspense, Fragment } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import ExecutionEnvironment from "exenv";
import axios from "axios";

// Components
import CustomMeta from "../components/CustomMeta/CustomMeta";
import config from "../utils/config";

const LoginOrSignup = lazy(() =>
  import(
    /* webpackChunkName: "LoginOrSignup" */ "../components/Login/LoginOrSignup"
  )
);

import { makeStyles } from "tss-react/mui";
import {
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Box,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Collapse,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import CommentIcon from "@mui/icons-material/Comment";
import StarIcon from "@mui/icons-material/Star";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// redux stuff
import { connect } from "react-redux";

const useStyles = makeStyles()((theme) => ({
  fullPageHeight: {
    minHeight: "100vh",
  },
  heading: {
    paddingTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  subTitle: {
    padding: "0px 30px 0px 30px",
    textAlign: "center",
    fontSize: 20,
  },
  loadingDiv: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  gridContainer: {
    display: "flex",
    justifyContent: "center",
    padding: `0 ${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)}`,
  },
  gridRoot: {
    flexGrow: 1,
    padding: theme.spacing(1),
    maxWidth: 1237,
  },
  featureCard: {
    marginBottom: theme.spacing(2),
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  upvoteButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 60,
  },
  upvoteCount: {
    fontSize: 20,
    fontWeight: "bold",
    marginTop: theme.spacing(0.5),
  },
  featureContent: {
    flexGrow: 1,
  },
  foundingPartnerBadge: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    fontSize: "0.7rem",
    height: 20,
    marginLeft: theme.spacing(1),
  },
  commentSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  comment: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  roadmapItem: {
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function FeatureRoadmap(props) {
  const { classes } = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [roadmapFeatures, setRoadmapFeatures] = useState([]);
  const [completedFeatures, setCompletedFeatures] = useState([]);
  const [expandedFeatureId, setExpandedFeatureId] = useState(null);
  const [newFeatureDialogOpen, setNewFeatureDialogOpen] = useState(false);
  const [roadmapDialogOpen, setRoadmapDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState(null);
  const [editFeatureDialogOpen, setEditFeatureDialogOpen] = useState(false);
  const [featureToEdit, setFeatureToEdit] = useState(null);
  const [selectedFeatureForRoadmap, setSelectedFeatureForRoadmap] =
    useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Loading states
  const [submittingFeature, setSubmittingFeature] = useState(false);
  const [deletingFeature, setDeletingFeature] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);
  const [deleteCommentDialogOpen, setDeleteCommentDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  // Form states
  const [newFeatureTitle, setNewFeatureTitle] = useState("");
  const [newFeatureDescription, setNewFeatureDescription] = useState("");
  const [editFeatureTitle, setEditFeatureTitle] = useState("");
  const [editFeatureDescription, setEditFeatureDescription] = useState("");
  const [commentText, setCommentText] = useState({});
  const [estimatedDate, setEstimatedDate] = useState("");

  useEffect(() => {
    loadFeatures();
    loadRoadmap();
    loadCompletedFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const token = localStorage.FBIdToken;
      const response = await axios.get(
        `${config.apiBaseURL}/api/features/getFeatures`,
        {
          headers: { Authorization: token },
        }
      );
      setFeatures(response.data);
    } catch (error) {
      console.error("Error loading features:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoadmap = async () => {
    try {
      const token = localStorage.FBIdToken;
      const response = await axios.get(
        `${config.apiBaseURL}/api/features/getRoadmap`,
        {
          headers: { Authorization: token },
        }
      );
      setRoadmapFeatures(response.data);
    } catch (error) {
      console.error("Error loading roadmap:", error);
    }
  };

  const loadCompletedFeatures = async () => {
    try {
      const token = localStorage.FBIdToken;
      const response = await axios.get(
        `${config.apiBaseURL}/api/features/getCompletedFeatures`,
        {
          headers: { Authorization: token },
        }
      );
      setCompletedFeatures(response.data);
    } catch (error) {
      console.error("Error loading completed features:", error);
    }
  };

  const handleSubmitFeature = async () => {
    if (!newFeatureTitle.trim()) return;

    try {
      setSubmittingFeature(true);
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/createFeature`,
        {
          title: newFeatureTitle,
          description: newFeatureDescription,
        },
        {
          headers: { Authorization: token },
        }
      );
      setNewFeatureDialogOpen(false);
      setNewFeatureTitle("");
      setNewFeatureDescription("");
      loadFeatures();
    } catch (error) {
      console.error("Error creating feature:", error);
    } finally {
      setSubmittingFeature(false);
    }
  };

  const handleUpvoteFeature = async (featureId, currentlyUpvoted) => {
    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/upvoteFeature`,
        {
          featureId,
          remove: currentlyUpvoted,
        },
        {
          headers: { Authorization: token },
        }
      );
      loadFeatures();
    } catch (error) {
      console.error("Error upvoting feature:", error);
    }
  };

  const handleDownvoteFeature = async (featureId, currentlyDownvoted) => {
    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/downvoteFeature`,
        {
          featureId,
          remove: currentlyDownvoted,
        },
        {
          headers: { Authorization: token },
        }
      );
      loadFeatures();
    } catch (error) {
      console.error("Error downvoting feature:", error);
    }
  };

  const handleUpvoteComment = async (
    featureId,
    commentId,
    currentlyUpvoted
  ) => {
    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/upvoteComment`,
        {
          featureId,
          commentId,
          remove: currentlyUpvoted,
        },
        {
          headers: { Authorization: token },
        }
      );
      loadFeatures();
    } catch (error) {
      console.error("Error upvoting comment:", error);
    }
  };

  const handleDownvoteComment = async (
    featureId,
    commentId,
    currentlyDownvoted
  ) => {
    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/downvoteComment`,
        {
          featureId,
          commentId,
          remove: currentlyDownvoted,
        },
        {
          headers: { Authorization: token },
        }
      );
      loadFeatures();
    } catch (error) {
      console.error("Error downvoting comment:", error);
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      setDeletingComment(true);
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/deleteComment`,
        {
          featureId: commentToDelete.featureId,
          commentId: commentToDelete.commentId,
        },
        {
          headers: { Authorization: token },
        }
      );
      setDeleteCommentDialogOpen(false);
      setCommentToDelete(null);
      loadFeatures();
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setDeletingComment(false);
    }
  };

  const handleSubmitComment = async (featureId) => {
    const text = commentText[featureId];
    if (!text || !text.trim()) return;

    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/addComment`,
        {
          featureId,
          text,
        },
        {
          headers: { Authorization: token },
        }
      );
      setCommentText({ ...commentText, [featureId]: "" });
      loadFeatures();
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const handleAddToRoadmap = async () => {
    if (!selectedFeatureForRoadmap || !estimatedDate) return;

    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/addToRoadmap`,
        {
          featureId: selectedFeatureForRoadmap,
          estimatedCompletionDate: estimatedDate,
        },
        {
          headers: { Authorization: token },
        }
      );
      setRoadmapDialogOpen(false);
      setSelectedFeatureForRoadmap(null);
      setEstimatedDate("");
      loadFeatures();
      loadRoadmap();
    } catch (error) {
      console.error("Error adding to roadmap:", error);
    }
  };

  const handleDeleteFeature = async () => {
    if (!featureToDelete) return;

    try {
      setDeletingFeature(true);
      const token = localStorage.FBIdToken;
      await axios.delete(
        `${config.apiBaseURL}/api/features/deleteFeature/${featureToDelete}`,
        {
          headers: { Authorization: token },
        }
      );
      setDeleteConfirmDialogOpen(false);
      setFeatureToDelete(null);
      loadFeatures();
    } catch (error) {
      console.error("Error deleting feature:", error);
    } finally {
      setDeletingFeature(false);
    }
  };

  const handleEditFeature = async () => {
    if (!featureToEdit || !editFeatureTitle.trim()) return;

    try {
      const token = localStorage.FBIdToken;
      await axios.put(
        `${config.apiBaseURL}/api/features/updateFeature`,
        {
          featureId: featureToEdit,
          title: editFeatureTitle,
          description: editFeatureDescription,
        },
        {
          headers: { Authorization: token },
        }
      );
      setEditFeatureDialogOpen(false);
      setFeatureToEdit(null);
      setEditFeatureTitle("");
      setEditFeatureDescription("");
      loadFeatures();
    } catch (error) {
      console.error("Error updating feature:", error);
    }
  };

  const handleRemoveFromRoadmap = async (featureId) => {
    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/removeFromRoadmap`,
        {
          featureId,
        },
        {
          headers: { Authorization: token },
        }
      );
      loadFeatures();
      loadRoadmap();
    } catch (error) {
      console.error("Error removing from roadmap:", error);
    }
  };

  const handleMarkAsDeployed = async (featureId) => {
    try {
      const token = localStorage.FBIdToken;
      await axios.post(
        `${config.apiBaseURL}/api/features/markAsDeployed`,
        {
          featureId,
        },
        {
          headers: { Authorization: token },
        }
      );
      loadFeatures();
      loadRoadmap();
      loadCompletedFeatures();
    } catch (error) {
      console.error("Error marking as deployed:", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleComments = (featureId) => {
    setExpandedFeatureId(expandedFeatureId === featureId ? null : featureId);
  };

  function getDashboardMarkup() {
    if (loading) {
      return (
        <div className={classes.loadingDiv}>
          <CircularProgress size={25} />
        </div>
      );
    } else if (!props.user.authenticated) {
      return (
        <Grid item xs={12}>
          <LoginOrSignup titleText="Log in to view and vote on features" />
        </Grid>
      );
    } else {
      return (
        <Fragment>
          <Grid item xs={12}>
            <Paper style={{ padding: 16 }}>
              <Tabs value={tabValue} onChange={handleTabChange} centered>
                <Tab label="Feature Requests" />
                <Tab label="Roadmap" />
                <Tab label="Completed Features" />
              </Tabs>
            </Paper>
          </Grid>

          {/* Feature Requests Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="h3">Feature Requests</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setNewFeatureDialogOpen(true)}
                  >
                    Request Feature
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {features.map((feature) => {
                  const userUpvoted = feature.upvotedBy?.includes(
                    props.user.credentials.userID
                  );
                  const userDownvoted = feature.downvotedBy?.includes(
                    props.user.credentials.userID
                  );
                  const isExpanded = expandedFeatureId === feature.id;
                  const netVotes =
                    (feature.upvotes || 0) - (feature.downvotes || 0);

                  return (
                    <Card key={feature.id} className={classes.featureCard}>
                      <CardContent>
                        <Box display="flex" alignItems="flex-start">
                          <Box className={classes.upvoteButton}>
                            <IconButton
                              color={userUpvoted ? "primary" : "default"}
                              onClick={() =>
                                handleUpvoteFeature(feature.id, userUpvoted)
                              }
                              size="small"
                            >
                              {userUpvoted ? (
                                <ThumbUpIcon />
                              ) : (
                                <ThumbUpOutlinedIcon />
                              )}
                            </IconButton>
                            <Typography className={classes.upvoteCount}>
                              {netVotes}
                            </Typography>
                            <IconButton
                              color={userDownvoted ? "error" : "default"}
                              onClick={() =>
                                handleDownvoteFeature(feature.id, userDownvoted)
                              }
                              size="small"
                            >
                              {userDownvoted ? (
                                <ThumbDownIcon />
                              ) : (
                                <ThumbDownOutlinedIcon />
                              )}
                            </IconButton>
                          </Box>

                          <Box
                            className={classes.featureContent}
                            ml={2}
                            flexGrow={1}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              mb={1}
                            >
                              <Typography variant="h4">
                                {feature.title}
                              </Typography>
                              {feature.status === "roadmap" && (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="On Roadmap"
                                  color="primary"
                                  size="small"
                                  className={classes.statusChip}
                                />
                              )}
                            </Box>

                            <Typography
                              variant="body2"
                              color="textSecondary"
                              gutterBottom
                            >
                              Requested by {feature.authorName}
                              {feature.authorIsFoundingPartner && (
                                <Chip
                                  icon={<StarIcon />}
                                  label="Founding Partner"
                                  size="small"
                                  className={classes.foundingPartnerBadge}
                                />
                              )}
                              {" • "}
                              {new Date(feature.createdAt).toLocaleDateString()}
                            </Typography>

                            <Typography variant="body1" paragraph>
                              {feature.description}
                            </Typography>

                            <Box display="flex" gap={1} mt={1}>
                              <Button
                                size="small"
                                startIcon={<CommentIcon />}
                                onClick={() => toggleComments(feature.id)}
                              >
                                {feature.comments?.length || 0} Comments
                              </Button>
                              {((props.user.credentials.role &&
                                props.user.credentials.role === "admin") ||
                                localStorage.getItem("pgrole") === "admin") &&
                                feature.status !== "roadmap" && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      setSelectedFeatureForRoadmap(feature.id);
                                      setRoadmapDialogOpen(true);
                                    }}
                                  >
                                    Add to Roadmap
                                  </Button>
                                )}
                              {((props.user.credentials.role &&
                                props.user.credentials.role === "admin") ||
                                localStorage.getItem("pgrole") === "admin") && (
                                <>
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                      setFeatureToEdit(feature.id);
                                      setEditFeatureTitle(feature.title);
                                      setEditFeatureDescription(
                                        feature.description
                                      );
                                      setEditFeatureDialogOpen(true);
                                    }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => {
                                      setFeatureToDelete(feature.id);
                                      setDeleteConfirmDialogOpen(true);
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                            </Box>

                            {/* Comments Section */}
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box className={classes.commentSection}>
                                <Typography variant="h5" gutterBottom>
                                  Comments
                                </Typography>

                                {/* Comment Input */}
                                <Box display="flex" gap={1} mb={2}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Add a comment..."
                                    value={commentText[feature.id] || ""}
                                    onChange={(e) =>
                                      setCommentText({
                                        ...commentText,
                                        [feature.id]: e.target.value,
                                      })
                                    }
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter") {
                                        handleSubmitComment(feature.id);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="contained"
                                    onClick={() =>
                                      handleSubmitComment(feature.id)
                                    }
                                  >
                                    Post
                                  </Button>
                                </Box>

                                {/* Comments List */}
                                {feature.comments?.map((comment) => {
                                  const commentUpvoted =
                                    comment.upvotedBy?.includes(
                                      props.user.credentials.userID
                                    );
                                  const commentDownvoted =
                                    comment.downvotedBy?.includes(
                                      props.user.credentials.userID
                                    );
                                  const commentNetVotes =
                                    (comment.upvotes || 0) -
                                    (comment.downvotes || 0);

                                  return (
                                    <Box
                                      key={comment.id}
                                      className={classes.comment}
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="flex-start"
                                      >
                                        <Box flexGrow={1}>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                          >
                                            {comment.authorName}
                                            {comment.authorIsFoundingPartner && (
                                              <Chip
                                                icon={<StarIcon />}
                                                label="Founding Partner"
                                                size="small"
                                                className={
                                                  classes.foundingPartnerBadge
                                                }
                                              />
                                            )}
                                            {" • "}
                                            {new Date(
                                              comment.createdAt
                                            ).toLocaleDateString()}
                                          </Typography>
                                          <Typography variant="body1">
                                            {comment.text}
                                          </Typography>
                                        </Box>
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          ml={2}
                                          gap={0.5}
                                        >
                                          <IconButton
                                            size="small"
                                            color={
                                              commentUpvoted
                                                ? "primary"
                                                : "default"
                                            }
                                            onClick={() =>
                                              handleUpvoteComment(
                                                feature.id,
                                                comment.id,
                                                commentUpvoted
                                              )
                                            }
                                          >
                                            {commentUpvoted ? (
                                              <ThumbUpIcon fontSize="small" />
                                            ) : (
                                              <ThumbUpOutlinedIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                          <Typography variant="body2">
                                            {commentNetVotes}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            color={
                                              commentDownvoted
                                                ? "error"
                                                : "default"
                                            }
                                            onClick={() =>
                                              handleDownvoteComment(
                                                feature.id,
                                                comment.id,
                                                commentDownvoted
                                              )
                                            }
                                          >
                                            {commentDownvoted ? (
                                              <ThumbDownIcon fontSize="small" />
                                            ) : (
                                              <ThumbDownOutlinedIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                          {((props.user.credentials.role &&
                                            props.user.credentials.role ===
                                              "admin") ||
                                            localStorage.getItem("pgrole") ===
                                              "admin") && (
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                setCommentToDelete({
                                                  featureId: feature.id,
                                                  commentId: comment.id,
                                                });
                                                setDeleteCommentDialogOpen(
                                                  true
                                                );
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Grid>
            </Grid>
          </TabPanel>

          {/* Roadmap Tab */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3" gutterBottom>
                  Product Roadmap
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  Here's what we're working on. Dates are estimates and may
                  change based on priorities.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {roadmapFeatures.map((feature) => (
                  <Card
                    key={feature.id}
                    className={classes.roadmapItem}
                    style={{ marginBottom: 16 }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <ScheduleIcon color="primary" />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          ml={1}
                        >
                          Estimated:{" "}
                          {new Date(
                            feature.estimatedCompletionDate
                          ).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography variant="h4" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1">
                        {feature.description}
                      </Typography>
                      <Box mt={2} display="flex" gap={1} alignItems="center">
                        <Chip
                          icon={<ThumbUpIcon />}
                          label={`${feature.upvotes || 0} votes`}
                          size="small"
                        />
                        {((props.user.credentials.role &&
                          props.user.credentials.role === "admin") ||
                          localStorage.getItem("pgrole") === "admin") && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleMarkAsDeployed(feature.id)}
                            >
                              Mark as Deployed
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() =>
                                handleRemoveFromRoadmap(feature.id)
                              }
                            >
                              Remove from Roadmap
                            </Button>
                          </>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {roadmapFeatures.length === 0 && (
                  <Paper style={{ padding: 32, textAlign: "center" }}>
                    <Typography variant="body1" color="textSecondary">
                      No features on the roadmap yet. Check back soon!
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </TabPanel>

          {/* Completed Features Tab */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3" gutterBottom>
                  Completed Features
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  Features that have been deployed to production.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {completedFeatures.map((feature) => (
                  <Card
                    key={feature.id}
                    className={classes.roadmapItem}
                    style={{ marginBottom: 16 }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <CheckCircleIcon color="success" />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          ml={1}
                        >
                          Deployed:{" "}
                          {new Date(feature.completedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography variant="h4" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1">
                        {feature.description}
                      </Typography>
                      <Box mt={2}>
                        <Chip
                          icon={<ThumbUpIcon />}
                          label={`${feature.upvotes || 0} votes`}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {completedFeatures.length === 0 && (
                  <Paper style={{ padding: 32, textAlign: "center" }}>
                    <Typography variant="body1" color="textSecondary">
                      No completed features yet. Check back soon!
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </TabPanel>

          {/* New Feature Dialog */}
          <Dialog
            open={newFeatureDialogOpen}
            onClose={() => setNewFeatureDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Request a New Feature</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Feature Title"
                fullWidth
                value={newFeatureTitle}
                onChange={(e) => setNewFeatureTitle(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Description"
                fullWidth
                multiline
                rows={4}
                value={newFeatureDescription}
                onChange={(e) => setNewFeatureDescription(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewFeatureDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitFeature}
                variant="contained"
                color="primary"
                disabled={submittingFeature || !newFeatureTitle.trim()}
                startIcon={submittingFeature && <CircularProgress size={20} />}
              >
                {submittingFeature ? "Submitting..." : "Submit"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add to Roadmap Dialog */}
          <Dialog
            open={roadmapDialogOpen}
            onClose={() => setRoadmapDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add Feature to Roadmap</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Estimated Completion Date"
                type="date"
                fullWidth
                value={estimatedDate}
                onChange={(e) => setEstimatedDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRoadmapDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddToRoadmap}
                variant="contained"
                color="primary"
              >
                Add to Roadmap
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmDialogOpen}
            onClose={() => setDeleteConfirmDialogOpen(false)}
          >
            <DialogTitle>Delete Feature Request?</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this feature request? This
                action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteFeature}
                variant="contained"
                color="error"
                disabled={deletingFeature}
                startIcon={deletingFeature && <CircularProgress size={20} />}
              >
                {deletingFeature ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Feature Dialog */}
          <Dialog
            open={editFeatureDialogOpen}
            onClose={() => setEditFeatureDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Edit Feature Request</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Feature Title"
                fullWidth
                value={editFeatureTitle}
                onChange={(e) => setEditFeatureTitle(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Description"
                fullWidth
                multiline
                rows={4}
                value={editFeatureDescription}
                onChange={(e) => setEditFeatureDescription(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditFeatureDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleEditFeature}
                variant="contained"
                color="primary"
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Comment Confirmation Dialog */}
          <Dialog
            open={deleteCommentDialogOpen}
            onClose={() => setDeleteCommentDialogOpen(false)}
          >
            <DialogTitle>Delete Comment?</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this comment? This action cannot
                be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteCommentDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteComment}
                variant="contained"
                color="error"
                disabled={deletingComment}
                startIcon={deletingComment && <CircularProgress size={20} />}
              >
                {deletingComment ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </Fragment>
      );
    }
  }

  return (
    <div className={classes.fullPageHeight}>
      <CustomMeta
        title="Feature Roadmap - Content Generator"
        description="View our product roadmap and vote on features."
        url="https://contentgenerator.io/feature-roadmap"
      />
      <div className={classes.heading}>
        <Typography variant="h1">Feature Roadmap</Typography>
      </div>
      <Typography className={classes.subTitle}>
        Help shape the future of Content Generator
      </Typography>
      <div className={classes.gridContainer}>
        {ExecutionEnvironment.canUseDOM && (
          <Suspense fallback={<div />}>
            <Grid
              container
              className={classes.gridRoot}
              spacing={2}
              direction="row"
              justifyContent="center"
            >
              {getDashboardMarkup()}
            </Grid>
          </Suspense>
        )}
      </div>
    </div>
  );
}

FeatureRoadmap.propTypes = {
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(FeatureRoadmap);
